import { okrDetailConstants } from '../constants/okr/okrDetails.constants';
import { krdetailConstants } from '../constants/krdetails.constants';
import { isProgressFilterUsed } from './okr';
import { isTimePeriodFilterApplied } from './timePeriodFilter.util';

const {
  TEAMS_VIEW,
  PEOPLE_VIEW,
  TEAM_SELECTION,
  USER_TEAM_TYPE_NONE,
  TEAM_SCOPE_ALL,
  TEAM_SCOPE_DEFAULT,
  GOAL_ACTIVE,
  EX_DATE_EXACT_MATCH,
  EX_DATE_SPAN_PERIOD,
  USE_SHARED_BUILDER,
  CATEGORIES_NONE,
  FILTER_PROGRESS_ALL,
} = okrDetailConstants;

export const OKR_URL_FILTER_KEYS = [
  'startDate',
  'endDate',
  'exDate',
  'status',
  'categories',
  'custom_tag_id',
  'performance',
  'customFields',
  'overdueKR',
  'metricStatus',
];

const DEFAULT_KR_STATUS = String(krdetailConstants.METRIC_ACTIVE);

export const isCloseKeyResultEnabled = () => !!window.wbConfig?.isCloseKeyResultEnabled;

/** The Key Result Status filter row is offered on the Team OKRs tab only. */
export const isKrStatusFilterEnabled = (pageType) => (
  isCloseKeyResultEnabled() && pageType === okrDetailConstants.PAGE_TYPE_TEAM_TAB_OKR
);

export const isNonDefaultKrStatusFilter = (metricStatus) => (
  Boolean(metricStatus) && String(metricStatus) !== DEFAULT_KR_STATUS
);

/** First saved Key Result Status, defaulting to Open; undefined when the flag is off. */
export const resolveSavedKrStatus = (...candidates) => {
  if (!isCloseKeyResultEnabled()) {
    return undefined;
  }
  return candidates.find(Boolean) || DEFAULT_KR_STATUS;
};

const selectedKrStatuses = (filterDataSet) => (
  String(filterDataSet?.metricStatus || DEFAULT_KR_STATUS).split(',')
);

const hasHiddenKeyResult = (metrics, isVisible) => metrics.some(
  (metric) => !isVisible(metric) || (metric.nested_krs || []).some((nested) => !isVisible(nested)),
);

// A parent that is itself hidden stays as context when a nested KR matches.
const keepVisibleKeyResults = (metrics, isVisible) => metrics
  .filter((metric) => isVisible(metric) || (metric.nested_krs || []).some(isVisible))
  .map((metric) => (metric.nested_krs
    ? { ...metric, nested_krs: metric.nested_krs.filter(isVisible) }
    : metric));

/**
 * Team OKRs tab: an Open Objective lists only KRs in the Key Result Status selection;
 * a Closed Objective lists all of its KRs.
 */
export const filterGoalKeyResultsByStatus = (goal, filterDataSet, pageType) => {
  const metrics = goal.goal_metrics || [];
  const isClosedObjective = Number(goal.status) === okrDetailConstants.GOAL_CLOSED;
  if (!isKrStatusFilterEnabled(pageType) || isClosedObjective) {
    return goal;
  }
  const statuses = selectedKrStatuses(filterDataSet);
  const isVisible = (metric) => statuses.includes(String(metric.status));
  if (!hasHiddenKeyResult(metrics, isVisible)) {
    return goal;
  }
  const visibleMetrics = keepVisibleKeyResults(metrics, isVisible);
  const hiddenCount = metrics.length - visibleMetrics.length;
  const totalCount = Number(goal.goal_metrics_count) || metrics.length;
  return {
    ...goal,
    goal_metrics: visibleMetrics,
    goal_metrics_count: Math.max(0, totalCount - hiddenCount),
  };
};

const GOAL_SUMMARY_PAGINATION_KEYS = ['paginate', 'goalLimit', 'goalOffset'];

const GOAL_SUMMARY_HEATMAP_EXTRA_KEYS = ['heatMap', 'htMapColCount'];

/**
 * Parses goalSummary params from the current URL (window.location.search).
 * @returns {Object} params object suitable for buildGoalSummaryUrlParams
 */
export const getGoalSummaryParamsFromCurrentUrl = () => {
  const params = {};
  try {
    const { search } = window.location;
    if (!search || search.length < 2) {
      return params;
    }
    search.substring(1).split('&').forEach((pair) => {
      const idx = pair.indexOf('=');
      if (idx < 0) return;
      const key = decodeURIComponent(pair.substring(0, idx));
      const val = decodeURIComponent(pair.substring(idx + 1).replace(/\+/g, ' '));
      if (key && key !== '_') {
        params[key] = val;
      }
    });
    GOAL_SUMMARY_PAGINATION_KEYS.forEach((key) => {
      delete params[key];
    });
  } catch (e) {
    console.error('getGoalSummaryParamsFromCurrentUrl:', e);
  }
  return params;
};

/**
 * Extracts goalSummary filter params from the goal-progress-filter-form (team/type scope).
 * @returns {Object} params object suitable for buildGoalSummaryUrlParams
 */
export const getGoalSummaryParamsFromForm = () => {
  const form = document.getElementById('goal-progress-filter-form');
  if (!form) {
    return {};
  }
  const params = {};
  const formData = new FormData(form);
  formData.forEach((value, name) => {
    if (name !== 'YII_CSRF_TOKEN') {
      params[name] = value;
    }
  });

  const teamPeopleText = document.querySelector('.teamPeopleText');
  const userTeamType = teamPeopleText?.getAttribute('userteamtype')
    || teamPeopleText?.getAttribute('userTeamType')
    || '';
  if (userTeamType && userTeamType !== String(USER_TEAM_TYPE_NONE)) {
    params.userTeamid = teamPeopleText?.getAttribute('value') || '';
    params.userTeamType = userTeamType;
    if (userTeamType == TEAM_SELECTION && params.userTeamid) {
      params.getNestedTeams = true;
    }
    params.team_s = TEAM_SCOPE_ALL;
    params.team_p = TEAM_SCOPE_DEFAULT;
  } else {
    const teamPeopleHidden = params.team_people_hidden
      || params.team_people
      || document.getElementById('team_people_hidden')?.getAttribute('value');
    const teamValue = teamPeopleText?.getAttribute('value') || TEAM_SCOPE_ALL;
    if (teamPeopleHidden == TEAMS_VIEW) {
      params.team_s = teamValue || TEAM_SCOPE_ALL;
      params.team_p = TEAM_SCOPE_DEFAULT;
    } else if (teamPeopleHidden == PEOPLE_VIEW) {
      params.team_s = TEAM_SCOPE_DEFAULT;
      params.team_p = teamValue || TEAM_SCOPE_ALL;
    } else {
      params.team_s = TEAM_SCOPE_ALL;
      params.team_p = TEAM_SCOPE_ALL;
    }
  }

  try {
    const urlCustomFields = new URLSearchParams(window.location.search).get('customFields');
    if (urlCustomFields) {
      params.customFields = urlCustomFields;
    }
  } catch (e) {
    // ignore
  }

  return params;
};

/**
 * Builds URL (GET) params from goalSummary filter state for hard reload support.
 * @param {Object} params - filter params (team_people, team_s, team_p, startDate, endDate, etc.)
 * @returns {Object} GET params for goalSummary URL
 */
export const buildGoalSummaryUrlParams = (params = {}) => {
  const urlParams = {};
  urlParams.team_people = params.team_people != null ? params.team_people : TEAMS_VIEW;
  if (urlParams.team_people == TEAMS_VIEW) {
    urlParams.team_s = params.team_s != null ? params.team_s : TEAM_SCOPE_ALL;
  } else {
    urlParams.team_p = params.team_p != null ? params.team_p : TEAM_SCOPE_ALL;
  }
  if (params.userTeamid && params.userTeamType) {
    urlParams.userTeamid = params.userTeamid;
    urlParams.userTeamType = params.userTeamType;
    if (params.getNestedTeams) {
      urlParams.getNestedTeams = true;
    }
  }
  urlParams.status = params.status != null ? params.status : String(GOAL_ACTIVE);
  urlParams.performance = params.performance != null ? params.performance : FILTER_PROGRESS_ALL;
  if (params.startDate) {
    urlParams.startDate = params.startDate;
  }
  if (params.endDate) {
    urlParams.endDate = params.endDate;
  }
  urlParams.exDate = params.exDate != null ? params.exDate : EX_DATE_EXACT_MATCH;
  if (params.categories != null && params.categories !== '' && params.categories !== CATEGORIES_NONE) {
    urlParams.categories = params.categories;
  }
  if (params.businessGrpId) {
    urlParams.businessGrpId = params.businessGrpId;
    urlParams.useSharedBuilder = USE_SHARED_BUILDER;
  }
  if (params.custom_tag_id) {
    urlParams.custom_tag_id = params.custom_tag_id;
  }
  if (params.customFields) {
    urlParams.customFields = params.customFields;
  }

  const currentParams = getGoalSummaryParamsFromCurrentUrl();
  ['cm', 'goalId', 'metricId', 'source'].forEach((key) => {
    const val = params[key] != null ? params[key] : currentParams[key];
    if (val != null && val !== '' && val !== '0') {
      urlParams[key] = val;
    }
  });

  return urlParams;
};

/**
 * Build preview export Back URL for the OKR heatmap from current page filter state.
 */
export const buildHeatmapPreviewBackUrl = () => {
  let params = getGoalSummaryParamsFromCurrentUrl();
  if (Object.keys(params).length === 0) {
    params = getGoalSummaryParamsFromForm();
  }
  const urlParams = buildGoalSummaryUrlParams(params);
  GOAL_SUMMARY_HEATMAP_EXTRA_KEYS.forEach((key) => {
    if (params[key] != null && params[key] !== '') {
      urlParams[key] = params[key];
    }
  });
  Object.keys(urlParams).forEach((key) => {
    const value = urlParams[key];
    if (value === '' || value === null || value === undefined) {
      delete urlParams[key];
    }
  });
  const baseUrl = window.wbConfig?.baseUrl || '';
  const queryString = new URLSearchParams(urlParams).toString();
  return queryString
    ? `${baseUrl}/goal/goalSummary?${queryString}`
    : `${baseUrl}/goal/goalSummary`;
};

/** Keep heatmap export Back link in sync with current filter state
 * (read by legacy goal-publish-print handler). */
export const syncHeatmapExportPreviewBackUrl = () => {
  const exportEl = document.querySelector('.publishHeatMap.goal-publish-print');
  if (!exportEl) {
    return;
  }
  exportEl.setAttribute('data-preview-back-url', buildHeatmapPreviewBackUrl());
};

export const isTruthyOverdueParam = (value) => (
  value === true || value === 1 || value === '1' || value === 'true'
);

export const isDirectDotedListPageType = (type) => (
  type === okrDetailConstants.PAGE_TYPE_MY_DIRECT_OKR
  || type === okrDetailConstants.PAGE_TYPE_MY_DIRECT_KR
);

/**
 * Resolve overdue toggle for Direct & Dotted list API (props, call options, then URL).
 */
export const resolveDirectDotedListDueStatus = (callOptions = {}, props = {}, isDirectDotedListTab = false) => {
  if (callOptions.dueStatus !== undefined) {
    return callOptions.dueStatus === true;
  }
  if (props.dueStatus === true) {
    return true;
  }
  if (isDirectDotedListTab === true && isTruthyOverdueParam(getFilterParamsFromUrl().overdueKR)) {
    return true;
  }
  return false;
};

/** True when a dueStatus prop change only mirrors URL hydration (no refetch). */
export const isDirectDotedDueStatusUrlHydration = (previousDueStatus, nextDueStatus) => {
  const urlOverdue = isTruthyOverdueParam(getFilterParamsFromUrl().overdueKR);
  if (urlOverdue && previousDueStatus !== true && nextDueStatus === true) {
    return true;
  }
  if (!urlOverdue && previousDueStatus === true && nextDueStatus !== true) {
    return true;
  }
  return false;
};

export const urlHasOkrFilters = () => {
  const url = new URL(window.location.href);
  return OKR_URL_FILTER_KEYS.some((key) => url.searchParams.has(key)
    && (key !== 'metricStatus' || isCloseKeyResultEnabled()));
};

export const isUiFiltersAppliedInUrl = () => {
  const url = new URL(window.location.href);
  return isTruthyOverdueParam(url.searchParams.get(okrDetailConstants.UI_FILTERS_APPLIED));
};

/**
 * Direct & Dotted list should strip objectives with no KRs after any filter is applied.
 */
export const shouldHideEmptyOkrsForListRequest = (apiParams = {}, context = {}) => {
  if (apiParams.uiFiltersApplied === true || apiParams.uiFiltersApplied === 1) {
    return true;
  }
  if (apiParams.fromFilter === true || apiParams.fromFilter === 1) {
    return true;
  }
  if (context.clientFilterApplied === true) {
    return true;
  }
  if (context.dueStatus === true || isTruthyOverdueParam(apiParams.overdueKR)) {
    return true;
  }
  if (context.appliedListFilters === true) {
    return true;
  }
  return urlHasOkrFilters();
};

export const applyHideEmptyOkrsToApiParams = (apiParams = {}, context = {}) => {
  if (shouldHideEmptyOkrsForListRequest(apiParams, context)) {
    apiParams.hide_empty_okrs = 1;
  } else {
    delete apiParams.hide_empty_okrs;
  }
  return apiParams;
};

export const getFilterParamsFromUrl = () => {
  const url = new URL(window.location.href);

  return {
    startDate: url.searchParams.get('startDate') || undefined,
    endDate: url.searchParams.get('endDate') || undefined,
    status: url.searchParams.get('status') || undefined,
    categories: url.searchParams.get('categories') || undefined,
    custom_tag_id: url.searchParams.get('custom_tag_id') || undefined,
    performance: url.searchParams.get('performance') || undefined,
    customFields: url.searchParams.get('customFields') || undefined,
    exDate: url.searchParams.get('exDate') || undefined,
    overdueKR: url.searchParams.get('overdueKR') || undefined,
    metricStatus: isCloseKeyResultEnabled()
      ? url.searchParams.get('metricStatus') || undefined
      : undefined,
  };
};

/**
 * Builds filterDataSet for Redux / API from params (multiple input shapes).
 */
export const buildFilterDataSet = (filterData) => {
  const {
    startDate,
    endDate,
    exDate,
    dateOKR,
    onlyOpenGoals,
    openOKR,
    onlyClosedGoals,
    closedOKR,
    status,
    categories,
    krTags,
    custom_tag_id,
    performance,
    customFields,
    overdueKR,
    metricStatus,
  } = filterData || {};
  const statusStr = typeof status === 'string' ? status : '';
  const hasExplicitStatus = statusStr.length > 0;
  let computedOnlyOpenGoals;
  let computedOnlyClosedGoals;
  if (hasExplicitStatus) {
    computedOnlyOpenGoals = statusStr.includes(String(okrDetailConstants.GOAL_ACTIVE));
    computedOnlyClosedGoals = statusStr.includes(String(okrDetailConstants.GOAL_CLOSED));
  } else {
    computedOnlyOpenGoals = openOKR ?? onlyOpenGoals ?? true;
    computedOnlyClosedGoals = closedOKR ?? onlyClosedGoals ?? false;
  }
  const customTagId = custom_tag_id ?? krTags?.map((tag) => tag.id).join(',');
  const statusCsv = computedOnlyOpenGoals && computedOnlyClosedGoals
    ? `${okrDetailConstants.GOAL_ACTIVE},${okrDetailConstants.GOAL_CLOSED}`
    : computedOnlyOpenGoals ? String(okrDetailConstants.GOAL_ACTIVE)
    : computedOnlyClosedGoals ? String(okrDetailConstants.GOAL_CLOSED)
    : String(okrDetailConstants.GOAL_ACTIVE);

  return {
    startDate,
    endDate,
    categories,
    exDate: exDate ?? (dateOKR === true ? EX_DATE_EXACT_MATCH : EX_DATE_SPAN_PERIOD),
    onlyOpenGoals: computedOnlyOpenGoals,
    onlyClosedGoals: computedOnlyClosedGoals,
    statusSet: computedOnlyOpenGoals && computedOnlyClosedGoals,
    status: statusCsv,
    krTags,
    custom_tag_id: customTagId,
    performance,
    customFields,
    overdueKR: overdueKR ? 1 : undefined,
    metricStatus,
  };
};

/**
 * Merges filterDataSet fields into a goal list API params object.
 */
export const applyFilterDataSetToApiParams = (newParams, filterDataSet, dueStatus = false) => {
  if (typeof filterDataSet === 'undefined' || filterDataSet === null || filterDataSet === '') {
    return newParams;
  }
  newParams.startDate = filterDataSet.startDate;
  newParams.endDate = filterDataSet.endDate;
  if (filterDataSet.exDate && filterDataSet.exDate != '') {
    newParams.exDate = filterDataSet.exDate;
  } else if (filterDataSet.exDate == 0 || filterDataSet.exDate == '') {
    delete newParams.exDate;
  }
  if (filterDataSet.onlyClosedGoals && filterDataSet.onlyOpenGoals) {
    newParams.status = '1,2';
  } else if (filterDataSet.onlyClosedGoals && !filterDataSet.onlyOpenGoals) {
    newParams.status = '2';
  } else if (!filterDataSet.onlyClosedGoals && filterDataSet.onlyOpenGoals) {
    newParams.status = '1';
  }
  if (filterDataSet.performance) {
    newParams.performance = filterDataSet.performance;
  }
  delete newParams.categories;
  if (filterDataSet.categories) {
    newParams.categories = filterDataSet.categories;
  }
  if (filterDataSet.custom_tag_id) {
    newParams.custom_tag_id = filterDataSet.custom_tag_id;
  }
  if (filterDataSet.customFields) {
    newParams.customFields = filterDataSet.customFields;
  }
  if (filterDataSet.metricStatus) {
    newParams.metricStatus = filterDataSet.metricStatus;
  }
  if (dueStatus === true) {
    newParams.overdueKR = 1;
  } else {
    delete newParams.overdueKR;
  }
  return newParams;
};

/** Keep overdueKR in the page URL in sync with the overdue checkbox (Direct & Dotted, etc.). */
/** Remove objective filter query params from the current page URL. */
export const clearOkrFiltersFromUrl = () => {
  const url = new URL(window.location.href);
  OKR_URL_FILTER_KEYS.forEach((key) => {
    url.searchParams.delete(key);
  });
  url.searchParams.delete(okrDetailConstants.UI_FILTERS_APPLIED);
  window.history.replaceState({}, '', url.toString());
};

export const syncOverdueKrInUrl = (enabled) => {
  const url = new URL(window.location.href);
  if (enabled === true) {
    url.searchParams.set('overdueKR', '1');
  } else {
    url.searchParams.delete('overdueKR');
  }
  window.history.replaceState({}, '', url.toString());
};

const PREVIEW_BACK_STRIP_PARAMS = [
  'jsonRes',
  'offset',
  'fromFilter',
  'hide_empty_okrs',
  'kr_filters',
  'qt',
];

/**
 * Build preview export Back URL for Direct & Dotted from Redux filter state (not window.location alone).
 */
export const buildDirectDotedPreviewBackUrl = (filterDataSet = {}, dueStatus = false, options = {}) => {
  const url = new URL(window.location.href);
  if (options.activeTab !== undefined && options.activeTab !== null) {
    url.searchParams.set('activeTab', String(options.activeTab));
  } else if (!url.searchParams.has('activeTab')) {
    url.searchParams.set('activeTab', '1');
  }

  const filterCount = Number(options.filterCount) || 0;
  const shouldIncludeFilters = options.clientFilterApplied === true
    || dueStatus === true
    || filterCount > 0
    || urlHasOkrFilters();

  if (shouldIncludeFilters) {
    const built = buildFilterDataSet(filterDataSet);
    const setOrDelete = (key, value) => {
      if (typeof value !== 'undefined' && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      } else {
        url.searchParams.delete(key);
      }
    };

    setOrDelete('startDate', built.startDate);
    setOrDelete('endDate', built.endDate);
    setOrDelete('status', built.status);
    setOrDelete('categories', built.categories);
    setOrDelete('custom_tag_id', built.custom_tag_id);
    setOrDelete('performance', built.performance);
    if (built.exDate != null && built.exDate !== '') {
      setOrDelete('exDate', String(built.exDate));
    } else {
      url.searchParams.delete('exDate');
    }
    if (typeof built.customFields !== 'undefined' && built.customFields !== null && built.customFields !== '') {
      const customFieldsValue = typeof built.customFields === 'string'
        ? built.customFields
        : JSON.stringify(built.customFields);
      setOrDelete('customFields', customFieldsValue);
    } else {
      url.searchParams.delete('customFields');
    }
    if (dueStatus === true) {
      url.searchParams.set('overdueKR', '1');
    } else {
      url.searchParams.delete('overdueKR');
    }
    if (options.clientFilterApplied === true || filterCount > 0) {
      url.searchParams.set(okrDetailConstants.UI_FILTERS_APPLIED, '1');
    } else {
      url.searchParams.delete(okrDetailConstants.UI_FILTERS_APPLIED);
    }
  } else {
    url.searchParams.delete(okrDetailConstants.UI_FILTERS_APPLIED);
  }

  PREVIEW_BACK_STRIP_PARAMS.forEach((key) => {
    url.searchParams.delete(key);
  });

  return url.toString();
};

/** teamExpCol 1 = KRs expanded (shown); 0 = collapsed (hidden) */
export const showGoalMetricsFromTeamExpCol = (teamExpCol) => (
  typeof teamExpCol === 'undefined' ? true : String(teamExpCol) === '1'
);

/** Shape used by legacy filterSelectedData / buildInitialValues openOKR fields */
export const filterSelectedDataFromFilterDataSet = (filterDataSet = {}) => ({
  startDate: filterDataSet.startDate ?? null,
  endDate: filterDataSet.endDate ?? null,
  exDate: filterDataSet.exDate ?? EX_DATE_EXACT_MATCH,
  status: filterDataSet.status ?? String(okrDetailConstants.GOAL_ACTIVE),
  categories: filterDataSet.categories ?? '',
  krtags: filterDataSet.krTags ?? [],
  performance: filterDataSet.performance ?? okrDetailConstants.FILTER_PROGRESS_DEFAULT,
  customFields: filterDataSet.customFields ?? '',
  openOKR: filterDataSet.onlyOpenGoals,
  closedOKR: filterDataSet.onlyClosedGoals,
  dateOKR: filterDataSet.exDate == EX_DATE_EXACT_MATCH,
  metricStatus: filterDataSet.metricStatus,
});

let directDotedOkrFetchGeneration = 0;

/** Invalidate in-flight Direct & Dotted OKR list responses (newer request started). */
export const bumpDirectDotedOkrFetchGeneration = () => {
  directDotedOkrFetchGeneration += 1;
  return directDotedOkrFetchGeneration;
};

export const isCurrentDirectDotedOkrFetch = (generation) => (
  generation === directDotedOkrFetchGeneration
);

/** Open-only is the default list; only non-default status selections count as a filter. */
export const isNonDefaultOkrStatusFilter = (status) => {
  const statusStr = typeof status === 'string' ? status : '';
  if (!statusStr) {
    return false;
  }
  if (statusStr === String(okrDetailConstants.GOAL_ACTIVE)) {
    return false;
  }
  return true;
};

/** Active filter count for the objective filter badge (filter apply payload shape). */
export const getOkrFilterCount = (payload) => {
  const {
    startDate, endDate, status, categories, custom_tag_id, customFields, metricStatus,
  } = payload || {};
  let count = 0;

  if (isTimePeriodFilterApplied(payload)) {
    count += 1;
  }
  if (isNonDefaultOkrStatusFilter(status)) {
    count += 1;
  }
  if (isNonDefaultKrStatusFilter(metricStatus)) {
    count += 1;
  }
  if (categories) {
    count += 1;
  }
  if (custom_tag_id) {
    count += 1;
  }
  if (isProgressFilterUsed(payload?.performance)) {
    count += 1;
  }
  if (customFields) {
    try {
      const parsed = JSON.parse(customFields);
      count += Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      // ignore invalid JSON
    }
  }
  return count;
};

export const buildDirectDotedFilterMeta = (params, dueStatus = false) => {
  const fromFilter = params.fromFilter === true || params.fromFilter === 1;
  if (!fromFilter) {
    return {};
  }
  const filterDataSet = buildFilterDataSet(params);
  if (dueStatus === true) {
    filterDataSet.overdueKR = 1;
  }
  return {
    fromFilter: true,
    filterDataSet,
    filterCount: getOkrFilterCount(params),
    dueStatus: dueStatus === true,
  };
};

/**
 * Since MR-8897 the team OKR endpoints return Open KRs only unless metricStatus is sent,
 * so a Closed Objective came back with no KRs (OKR-11467). The Teams page shows the KRs
 * that match the selected Objective Status. An explicit metricStatus is left untouched.
 */
export const withMirroredMetricStatus = (qstrOrParams) => {
  if (typeof qstrOrParams === 'string') {
    const search = new URLSearchParams(qstrOrParams);
    const status = search.get('status');
    if (!status || search.has('metricStatus')) {
      return qstrOrParams;
    }
    const separator = qstrOrParams.includes('?') ? '&' : '?';
    return `${qstrOrParams}${separator}metricStatus=${encodeURIComponent(status)}`;
  }
  if (!qstrOrParams || !qstrOrParams.status || qstrOrParams.metricStatus) {
    return qstrOrParams;
  }
  return { ...qstrOrParams, metricStatus: qstrOrParams.status };
};
